import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from './Navbar';
import API_ENDPOINTS from '../config';
import { authenticatedFetch, authenticatedFetchJson } from '../utils/api.js';

export default function InvoiceCancel() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const fetchInvoice = useCallback(async () => {
    try {
      const data = await authenticatedFetchJson(`${API_ENDPOINTS.INVOICE_USER_BY_ID}/${id}`);
      setInvoice(data.invoice);
    } catch (error) {
      console.error('Error fetching invoice:', error);
      setError(t('invoiceCancel.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  const handleCancelInvoice = async () => {
    setCancelling(true);
    try {
      const response = await authenticatedFetch(`${API_ENDPOINTS.INVOICE_CONFIRM_PAYMENT}/${id}/cancel`, {
        method: 'POST'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel invoice');
      }

      const data = await response.json();
      window.alert(data.message);
      navigate('/dashboard');
    } catch (error) {
      console.error('Error cancelling invoice:', error);
      window.alert(error.message || t('invoiceCancel.errorCancelling'));
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{ textAlign: 'center', color: 'white' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
            <div style={{ fontSize: '1.5rem' }}>{t('invoiceCancel.loadingInvoice')}</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div>
        <Navbar />
        <div style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{ textAlign: 'center', color: 'white' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
            <div style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>{error || t('invoiceCancel.invoiceNotFound')}</div>
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                padding: '1rem 2rem',
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                border: '2px solid white',
                borderRadius: '50px',
                fontSize: '1rem',
                cursor: 'pointer'
              }}
            >
              {t('invoiceCancel.backToDashboard')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (invoice.status !== 'pending') {
    return (
      <div>
        <Navbar />
        <div style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{ 
            textAlign: 'center', 
            background: 'rgba(255,255,255,0.95)',
            padding: '3rem',
            borderRadius: '20px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
            <h2 style={{ fontSize: '1.5rem', color: '#374151', marginBottom: '1rem' }}>
              {t('invoiceCancel.cannotCancel')}
            </h2>
            <p style={{ color: '#6b7280', marginBottom: '2rem' }}>
              {t('invoiceCancel.cannotCancelMessage', { status: invoice.status })}
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                padding: '1rem 2rem',
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '50px',
                fontSize: '1rem',
                cursor: 'pointer'
              }}
            >
              {t('invoiceCancel.backToDashboard')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div style={{ 
          background: 'rgba(255,255,255,0.95)',
          borderRadius: '20px',
          padding: '3rem',
          maxWidth: '600px',
          width: '100%',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🗑️</div>
          
          <h1 style={{ 
            fontSize: '2rem', 
            color: '#374151', 
            marginBottom: '1rem',
            fontWeight: 700
          }}>
            {t('invoiceCancel.cancelInvoice')}
          </h1>
          
          <p style={{ 
            color: '#6b7280', 
            marginBottom: '2rem',
            fontSize: '1.1rem'
          }}>
            {t('invoiceCancel.confirmCancel')}
          </p>

          {/* Invoice Details */}
          <div style={{
            background: '#f8f9fa',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '2rem',
            textAlign: 'left'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#374151' }}>{t('invoiceCancel.invoiceDetails')}</h3>
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#374151' }}>
                <strong>{t('invoiceCancel.invoiceNumber')}:</strong> {invoice.invoiceNumber}
              </p>
              <p style={{ margin: 0, fontSize: '14px', color: '#374151' }}>
                <strong>{t('invoiceCancel.amount')}:</strong> {invoice.amount} {invoice.currency}
              </p>
              <p style={{ margin: 0, fontSize: '14px', color: '#374151' }}>
                <strong>{t('invoiceCancel.description')}:</strong> {invoice.description}
              </p>
              <p style={{ margin: 0, fontSize: '14px', color: '#374151' }}>
                <strong>{t('invoiceCancel.dueDate')}:</strong> {new Date(invoice.dueDate).toLocaleDateString('sr-RS')}
              </p>
              <p style={{ margin: 0, fontSize: '14px', color: '#374151' }}>
                <strong>{t('invoiceCancel.status')}:</strong> {invoice.status}
              </p>
            </div>
          </div>

          <div style={{ 
            display: 'flex', 
            gap: '1rem', 
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                padding: '1rem 2rem',
                background: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '50px',
                fontSize: '1rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.target.style.background = '#4b5563'}
              onMouseLeave={(e) => e.target.style.background = '#6b7280'}
            >
              {t('invoiceCancel.keepInvoice')}
            </button>
            
            <button
              onClick={handleCancelInvoice}
              disabled={cancelling}
              style={{
                padding: '1rem 2rem',
                background: cancelling ? '#fca5a5' : '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '50px',
                fontSize: '1rem',
                cursor: cancelling ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                if (!cancelling) e.target.style.background = '#dc2626';
              }}
              onMouseLeave={(e) => {
                if (!cancelling) e.target.style.background = '#ef4444';
              }}
            >
              {cancelling ? t('invoiceCancel.cancelling') : t('invoiceCancel.yesCancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
